import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';

import { UseOutletContext } from '../Main';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { CategoryDA } from '../../../services/admin/category.service';
import { AccountingAccountDA } from '../../../services/admin/accounting.account.service';
import { ProductDA } from '../../../services/admin/product.service';
import { ServiceDA } from '../../../services/admin/service.service';
import { StorageCenterDA } from '../../../services/admin/storage.center.service';
import { AppService } from '../../../models/app.service.model';
import { CompanyAccountingAccounts } from '../../../models/company.accounting.accounts.model';
import { CompanyCategories } from '../../../models/company.categories.model';
import { AccountingAccount } from '../../../models/accounting.account.model';
import { Error } from '../../../models/error.model';
import { Company }  from '../../../models/company.model';
import { StorageCenter } from '../../../models/storage.center.model';
import { Settings } from '../../../models/settings.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { Category } from '../../../models/category.model';
import { Service } from '../../../models/service.model';
import { Product } from '../../../models/product.model';
import { getError } from '../../../languages/translations/response';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { Brand, BrandsData } from '../../../models/brands.inventory.model';
import { BrandDA } from '../../../services/admin/brands.inventory.service';
import { usePagination } from '../../../hooks/usePagination';

export interface InventoryPageProps {};

type ContexType = {
  setRoute: Dispatch<SetStateAction<{path: {root: string, branch: string} | null, company: boolean} | null>>,
  appServiceForUser: AppService | null,
  companyForUser: Company | null,
  settingsForCompany: Settings | null,
  servicesForCompany: Service[] | null,
  productsForCompany: Product[] | null,
  brandsForCompany: Brand[] | null,
  storageCentersForCompany: StorageCenter[] | null,
  categoriesForCompany: Category[] | null,
  accountingExpenseAccountsForCompany: AccountingAccount[] | null,
  accountingVatAccountsForCompany: AccountingAccount[] | null,
  accountingIncomeAccountsForCompany: AccountingAccount[] | null,
  setCategoriesForCompany: Dispatch<SetStateAction<Category[] | null>>,
  setAccountingExpenseAccountsForCompany: Dispatch<SetStateAction<AccountingAccount[] | null>>,
  setAccountingVatAccountsForCompany: Dispatch<SetStateAction<AccountingAccount[] | null>>,
  setAccountingIncomeAccountsForCompany: Dispatch<SetStateAction<AccountingAccount[] | null>>,
  loadSettingsForCompany: Function,
  loadServicesForCompany: Function,
  loadProductsForCompany: Function,
  loadStorageCentersForCompany: Function,
  loadAccountingExpenseAccountsForCompany: Function,
  loadAccountingVatAccountsForCompany: Function,
  loadAccountingIncomeAccountsForCompany: Function,
  loadBrandsForCompany: Function
};

let errorResponse: Error, categoriesResponse: CompanyCategories, accountingExpenseAccountsResponse: CompanyAccountingAccounts, accountingVatAccountsResponse: CompanyAccountingAccounts, accountingIncomeAccountsResponse: CompanyAccountingAccounts, brandsResponse: BrandsData;

const InventoryPage: React.FunctionComponent<InventoryPageProps> = props => {
  const {setRoute, appServiceForUser, companyForUser, settingsForCompany, servicesForCompany, productsForCompany, storageCentersForCompany, loadSettingsForCompany, loadServicesForCompany, loadProductsForCompany, loadStorageCentersForCompany} = UseOutletContext()
  const {lang} = StateLanguage()

  const [localProducts, setLocalProducts] = useState<Product[] | null>(null)
  const [localServices, setLocalServices] = useState<Service[] | null>(null)
  const [localStorageCenters, setLocalStorageCenters] = useState<StorageCenter[] | null>(null)
  const [categoriesForCompany, setCategoriesForCompany] = useState<Category[] | null>(null)
  const [accountingExpenseAccountsForCompany, setAccountingExpenseAccountsForCompany] = useState<AccountingAccount[] | null>(null)
  const [accountingVatAccountsForCompany, setAccountingVatAccountsForCompany] = useState<AccountingAccount[] | null>(null)
  const [accountingIncomeAccountsForCompany, setAccountingIncomeAccountsForCompany] = useState<AccountingAccount[] | null>(null)
  const [brandsForCompany, setBrandsForCompany] = useState<Brand[]>([])
  const { pagination: brandPag, setPage: setBrandPage, setPaginationFromBackend: setBrandPagData } = usePagination();

  const loadLocalProducts = async (id_company: string) => {
    await ProductDA.getProducts(id_company, '', '', '', '').then((response) => {
      if (response.status === 200) {
        const data = response.data
        setLocalProducts(data?.items ?? data?.products ?? [])
      } else {
        setLocalProducts([])
      }
    }).catch(() => {
      setLocalProducts([])
    })
  }

  const loadLocalServices = async (id_company: string) => {
    await ServiceDA.getServices(id_company, '', '', '', '').then((response) => {
      if (response.status === 200) {
        const data = response.data
        setLocalServices(data?.items ?? data?.services ?? [])
      } else {
        setLocalServices([])
      }
    }).catch(() => {
      setLocalServices([])
    })
  }

  const loadLocalStorageCenters = async (id_company: string) => {
    await StorageCenterDA.getStorageCenters(id_company, '', '', '').then((response) => {
      if (response.status === 200) {
        const data = response.data
        setLocalStorageCenters(data?.items ?? data?.storage_centers ?? [])
      } else {
        setLocalStorageCenters([])
      }
    }).catch(() => {
      setLocalStorageCenters([])
    })
  }

  const loadCategoriesForCompany = async (id_company: string) => {
    await CategoryDA.getCategories(id_company, '', '', '').then( (response) => {
      if (response.status === 200) {
        categoriesResponse = response.data?.data ?? response.data
        setCategoriesForCompany(categoriesResponse.items ?? categoriesResponse.categories ?? [])
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadAccountingExpenseAccountsForCompany = async (id_company: string) => {
    await AccountingAccountDA.getAccountingAccounts(id_company, [], ['4','6'], []).then( (response) => {
      if (response.status === 200) {
        accountingExpenseAccountsResponse = response.data?.data ?? response.data
        setAccountingExpenseAccountsForCompany(accountingExpenseAccountsResponse.items ?? accountingExpenseAccountsResponse.accounting_accounts ?? [])
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadAccountingVatAccountsForCompany = async (id_company: string) => {
    await AccountingAccountDA.getAccountingAccounts(id_company, ['1170','1171'], [], []).then( (response) => {
      if (response.status === 200) {
        accountingVatAccountsResponse = response.data?.data ?? response.data
        setAccountingVatAccountsForCompany(accountingVatAccountsResponse.items ?? accountingVatAccountsResponse.accounting_accounts ?? [])
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadAccountingIncomeAccountsForCompany = async (id_company: string) => {
    await AccountingAccountDA.getAccountingAccounts(id_company, [], ['3'], []).then( (response) => {
      if (response.status === 200) {
        accountingIncomeAccountsResponse = response.data?.data ?? response.data
        setAccountingIncomeAccountsForCompany(accountingIncomeAccountsResponse.items ?? accountingIncomeAccountsResponse.accounting_accounts ?? [])
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadBrandsForCompany = async (id_company: string, page: number = 1) => {
    await BrandDA.getBrands(id_company, page, brandPag.limit).then( (response) => {
      if (response.status === 200) {
        const data = response.data as BrandsData;
        setBrandsForCompany(data.items ?? []);
        setBrandPagData(data.pagination);
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  useEffect( () => {
    if (companyForUser) {
      setCategoriesForCompany(null)
      setAccountingExpenseAccountsForCompany(null)
      setAccountingVatAccountsForCompany(null)
      setAccountingIncomeAccountsForCompany(null)
      loadCategoriesForCompany(companyForUser.id)
      loadAccountingExpenseAccountsForCompany(companyForUser.id)
      loadAccountingVatAccountsForCompany(companyForUser.id)
      loadAccountingIncomeAccountsForCompany(companyForUser.id)
      loadLocalProducts(companyForUser.id)
      loadLocalServices(companyForUser.id)
      loadLocalStorageCenters(companyForUser.id)
      loadBrandsForCompany(companyForUser.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  return (
    <Outlet context={{setRoute, appServiceForUser, companyForUser, settingsForCompany, servicesForCompany: servicesForCompany ?? localServices, productsForCompany: productsForCompany ?? localProducts, storageCentersForCompany: storageCentersForCompany ?? localStorageCenters, categoriesForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingIncomeAccountsForCompany, brandsForCompany, setCategoriesForCompany, setAccountingExpenseAccountsForCompany, setAccountingVatAccountsForCompany, setAccountingIncomeAccountsForCompany, setBrandsForCompany, loadSettingsForCompany, loadServicesForCompany, loadProductsForCompany, loadAccountingExpenseAccountsForCompany, loadAccountingVatAccountsForCompany, loadAccountingIncomeAccountsForCompany, loadStorageCentersForCompany, loadBrandsForCompany}}></Outlet>
  );
}

export function UseOutletContextInventory() {
  return useOutletContext<ContexType>()
};

export default InventoryPage;
